/**
=========================================================
* Material Dashboard 2 PRO React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Autocomplete from "@mui/material/Autocomplete";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDEditor from "components/MDEditor";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";

import cities from "data/cities.json";

// NewProduct page components
import FormField from "layouts/ecommerce/products/edit-product/components/FormField";

import EventCategories from "../../modals/Categories";
import EventPlaces from "../../modals/Places";
import EventSieges from "../../modals/Sieges";
import EventStands from "../../modals/Stands";
import EventSocial from "../../modals/Socials";

import { getAllCategoriesApi } from "api/categories";
import { getAllPlacesApi } from "api/places";
import { getAllStandsApi } from "api/stands";
import { getAllSiegesApi } from "api/sieges";
import { getAllSocialsApi } from "api/socials";

import { useParams } from "react-router-dom";
import { getOneEventApi } from "api/events";

import Icon from "@mui/material/Icon";
import EventSelectedSieges from "../../modals/Sieges/selectedSieges";
import EventSelectedStands from "../../modals/Stands/selectedStands";
import EventSelectedPlaces from "../../modals/Places/selectedPlaces";
import EventSelectedSocial from "../../modals/Socials/selectedSocials";
import Input from "@mui/material/Input";

import CinemaSelectedSieges from "../CinemaFormFields";

import { v4 as uuidv4 } from "uuid";
import Swal from "sweetalert2";

function FormFields({ formValues }) {
  const { eventID } = useParams();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.only("sm"));

  const [open, setOpen] = useState(false);
  const [openEventPlaces, setOpenEventPlaces] = useState(false);
  const [openEventSieges, setOpenEventSieges] = useState(false);
  const [openEventStand, setOpenEventStand] = useState(false);
  const [openEventSocial, setOpenEventSocial] = useState(false);

  const [categoriesData, setCategoriesData] = useState([]);
  const [placesData, setPlacesData] = useState([]);
  const [standsData, setStandsData] = useState([]);
  const [siegesData, setSiegesData] = useState([]);
  const [socialsData, setSocialsData] = useState([]);

  const [openEventSelectedPlaces, setOpenEventSelectedPlaces] = useState(false);
  const [openEventSelectedSieges, setOpenEventSelectedSieges] = useState(false);
  const [openEventSelectedStand, setOpenEventSelectedStand] = useState(false);
  const [openEventSelectedSocial, setOpenEventSelectedSocial] = useState(false);
  const [isCinemaMode, setIsCinemaMode] = useState(false);
  const [isCirqueMode, setIsCirqueMode] = useState(false);
  const [showtimes, setShowtimes] = useState([
    {
      generatedId: uuidv4(),
      startDate: "",
      startTime: "",
      endDate: "",
      endTime: "",
      eventDate: "",
      eventTime: "",
      eventDoorClosingDate: "",
      eventDoorClosingTime: "",
      prices: [],
      isSoldOut: false,
    },
  ]);

  const initialShowtimesRef = useRef([]);

  // const [eventDetails, setEventDetails] = useState(null)

  const [formDataValues, setFormDataValues] = useState({
    eventTitle: formValues?.eventTitle || "",
    promoVideo: formValues?.promoVideo || "",
    address: formValues?.address || "",
    city: formValues?.city || "",
    eventPlace: formValues?.eventPlace || [],
    eventCategory: formValues?.eventCategory || [],
    eventStand: formValues?.eventStand || [],
    eventSiege: formValues?.eventSiege || [],
    socialLink: formValues?.socialLink || [],
    startDate: formValues?.startDate,
    startTime: formValues?.startTime,
    endDate: formValues?.endDate,
    endTime: formValues?.endTime,
    eventDate: formValues?.eventDate,
    eventTime: formValues?.eventTime,

    eventDoorClosingDate: formValues?.eventDoorClosingDate || "",
    eventDoorClosingTime: formValues?.eventDoorClosingTime || "",

    onlineReservation: true,
    isSelectetivePlaces: false,
    seatIoId: formValues?.seatIoId || "",
    isSoldOut: formValues?.isSoldOut || false,
  });

  useEffect(() => {
    getInitialData();
  }, [eventID]);

  const getInitialData = async () => {
    try {
      const categories = await getAllCategoriesApi();
      setCategoriesData(categories);

      const places = await getAllPlacesApi();
      const stands = await getAllStandsApi();
      const socials = await getAllSocialsApi();

      const oneEventDetails = await getOneEventApi(eventID);

      const formValueObject = {
        eventTitle: oneEventDetails?.eventTitle,
        promoVideo: oneEventDetails?.promoVideo,
        address: oneEventDetails?.address,
        socialLink: oneEventDetails?.socialLink,
        eventPlace: oneEventDetails?.eventPlace,
        eventCategory: oneEventDetails?.eventCategory,
        eventStand: oneEventDetails?.eventStand,
        eventSiege: oneEventDetails?.eventSiege,
        startDate: oneEventDetails.startDate ? oneEventDetails.startDate.split("T")[0] : "",
        startTime: oneEventDetails.startDate
          ? oneEventDetails.startDate.split("T")[1]?.split(":").slice(0, 2).join(":")
          : "",
        endDate: oneEventDetails.endDate ? oneEventDetails.endDate.split("T")[0] : "",
        endTime: oneEventDetails.endDate
          ? oneEventDetails.endDate.split("T")[1]?.split(":").slice(0, 2).join(":")
          : "",
        eventDate: oneEventDetails.eventDate ? oneEventDetails.eventDate.split("T")[0] : "",
        eventTime: oneEventDetails.eventDate
          ? oneEventDetails.eventDate.split("T")[1]?.split(":").slice(0, 2).join(":")
          : "",

        eventDoorClosingDate: oneEventDetails?.eventCloseDoorDate
          ? oneEventDetails?.eventCloseDoorDate?.split("T")[0]
          : "",
        eventDoorClosingTime: oneEventDetails?.eventCloseDoorDate
          ? oneEventDetails?.eventCloseDoorDate?.split("T")[1]?.split(":").slice(0, 2).join(":")
          : "",

        onlineReservation: oneEventDetails?.onlineReservation,
        isSelectetivePlaces: oneEventDetails?.isSelectetivePlaces,
        seatIoId: oneEventDetails?.seatIoId,
        isSoldOut: oneEventDetails?.isSoldOut,
        isCinema: oneEventDetails?.isCinema || false,
        isCirque: oneEventDetails?.isCirque || false,
        city: oneEventDetails?.city || "",
      };

      setFormDataValues(formValueObject);

      setPlacesData([...(oneEventDetails?.eventPlace || []), ...places]);
      setStandsData([...(oneEventDetails?.eventStand || []), ...stands]);
      setSiegesData(oneEventDetails?.eventSiege || []);
      setSocialsData([...(oneEventDetails?.socialLink || []), ...socials]);

      let initialShowtimes = [];
      let initialIsCinemaMode = oneEventDetails?.isCinema || false;
      let initialIsCirqueMode = oneEventDetails?.isCirque || false;

      if ((initialIsCinemaMode || initialIsCirqueMode) && Array.isArray(oneEventDetails?.eventDateList)) {
        initialShowtimes = oneEventDetails.eventDateList.map((item) => ({
          isSoldOut: item.isSoldOut,
          startDate: item.startDate ? item.startDate.split("T")[0] : "",
          startTime: item.startDate
            ? item.startDate.split("T")[1]?.split(":").slice(0, 2).join(":")
            : "",
          endDate: item.endDate ? item.endDate.split("T")[0] : "",
          endTime: item.endDate ? item.endDate.split("T")[1]?.split(":").slice(0, 2).join(":") : "",
          eventDate: item.eventDate ? item.eventDate.split("T")[0] : "",
          eventTime: item.eventDate
            ? item.eventDate.split("T")[1]?.split(":").slice(0, 2).join(":")
            : "",
          eventDoorClosingDate: item.eventCloseDoorDate
            ? item.eventCloseDoorDate.split("T")[0]
            : "",
          eventDoorClosingTime: item.eventCloseDoorDate
            ? item.eventCloseDoorDate.split("T")[1]?.split(":").slice(0, 2).join(":")
            : "",
          prices: item.prices,
          generatedId: item.generatedId,
          ...(item._id && { _id: item._id }),
        }));
        if (initialShowtimes.length === 0) {
          initialShowtimes.push({
            generatedId: uuidv4(),
            startDate: "",
            startTime: "",
            endDate: "",
            endTime: "",
            eventDate: "",
            eventTime: "",
            eventDoorClosingDate: "",
            eventDoorClosingTime: "",
            prices: [],
            isSoldOut: false,
          });
        }
      } else if (initialIsCinemaMode || initialIsCirqueMode) {
        initialShowtimes.push({
          generatedId: uuidv4(),
          startDate: "",
          startTime: "",
          endDate: "",
          endTime: "",
          eventDate: "",
          eventTime: "",
          eventDoorClosingDate: "",
          eventDoorClosingTime: "",
          prices: [],
          isSoldOut: false,
        });
      }

      setIsCinemaMode(initialIsCinemaMode);
      setIsCirqueMode(initialIsCirqueMode);
      setShowtimes(initialShowtimes);
      initialShowtimesRef.current = initialShowtimes.map((s) => ({ ...s }));

      formValues({
        ...formValueObject,
        isCinema: initialIsCinemaMode,
        isCirque: initialIsCirqueMode,
        eventDateList: buildEventDateList(initialShowtimes, initialIsCinemaMode, initialIsCirqueMode),
      });
    } catch (err) {
      console.log(err);
    }
  };

  const handleClose = (params) => {
    setOpen(false);
  };
  const handleCloseEventPlaces = (params) => {
    setOpenEventPlaces(false);
  };
  const handleCloseEventSieges = (params) => {
    setOpenEventSieges(false);
  };
  const handleCloseEventStands = (params) => {
    setOpenEventStand(false);
  };
  const handleCloseEventSocial = (params) => {
    setOpenEventSocial(false);
  };

  const handleAddNewCategoryModal = () => {
    setOpen(true);
  };
  const handleAddNewPlaceModal = () => {
    setOpenEventPlaces(true);
  };
  const handleAddNewSiegeModal = () => {
    setOpenEventSieges(true);
  };
  const handleAddNewResellerModal = () => {
    setOpenEventStand(true);
  };
  const handleAddNewsocialModal = () => {
    setOpenEventSocial(true);
  };

  const handleCloseSelectedEventPlaces = (params) => {
    setOpenEventSelectedPlaces(false);
  };
  const handleCloseSelectedEventSieges = (params) => {
    setOpenEventSelectedSieges(false);
  };
  const handleCloseSelectedEventStands = (params) => {
    setOpenEventSelectedStand(false);
  };
  const handleCloseSelectedEventSocial = (params) => {
    setOpenEventSelectedSocial(false);
  };

  const handleNewPlaceData = (data) => {
    setPlacesData((prevData) => [...prevData, data]);
  };
  const buildEventDateList = (currentShowtimes, cinemaMode, cirqueMode) => {
    if ((!cinemaMode && !cirqueMode) || !Array.isArray(currentShowtimes)) {
      return [];
    }

    return currentShowtimes.map((showtime) => {
      let processedCloseDoorDate = null;
      if (showtime.eventDoorClosingDate) {
        const [year, month, day] = showtime.eventDoorClosingDate.split("-").map(Number);
        const [hour, minute] = showtime.eventDoorClosingTime
          ? showtime.eventDoorClosingTime.split(":").map(Number)
          : [0, 0];
        const dateObjUtc = new Date(Date.UTC(year, month - 1, day, hour, minute));
        if (!isNaN(dateObjUtc.getTime())) {
          processedCloseDoorDate = dateObjUtc.toISOString();
        } else {
          console.error(
            "DEBUG_DATE_ERROR: Invalid Date created for eventCloseDoorDate with values:",
            showtime.eventDoorClosingDate,
            showtime.eventDoorClosingTime
          );
        }
      }

      let processedStartDate = null;
      if (showtime.startDate) {
        const [year, month, day] = showtime.startDate.split("-").map(Number);
        const [hour, minute] = showtime.startTime
          ? showtime.startTime.split(":").map(Number)
          : [0, 0];
        const dateObjUtc = new Date(Date.UTC(year, month - 1, day, hour, minute));
        if (!isNaN(dateObjUtc.getTime())) {
          processedStartDate = dateObjUtc.toISOString();
        } else {
          console.error(
            "DEBUG_DATE_ERROR: Invalid Date created for startDate with values:",
            showtime.startDate,
            showtime.startTime
          );
        }
      }

      let processedEndDate = null;
      if (showtime.endDate) {
        const [year, month, day] = showtime.endDate.split("-").map(Number);
        const [hour, minute] = showtime.endTime ? showtime.endTime.split(":").map(Number) : [0, 0];
        const dateObjUtc = new Date(Date.UTC(year, month - 1, day, hour, minute));
        if (!isNaN(dateObjUtc.getTime())) {
          processedEndDate = dateObjUtc.toISOString();
        } else {
          console.error(
            "DEBUG_DATE_ERROR: Invalid Date created for endDate with values:",
            showtime.endDate,
            showtime.endTime
          );
        }
      }

      let processedEventDate = null;
      if (showtime.eventDate) {
        const [year, month, day] = showtime.eventDate.split("-").map(Number);
        const [hour, minute] = showtime.eventTime
          ? showtime.eventTime.split(":").map(Number)
          : [0, 0];
        const dateObjUtc = new Date(Date.UTC(year, month - 1, day, hour, minute));
        if (!isNaN(dateObjUtc.getTime())) {
          processedEventDate = dateObjUtc.toISOString();
        } else {
          console.error(
            "DEBUG_DATE_ERROR: Invalid Date created for eventDate with values:",
            showtime.eventDate,
            showtime.eventTime
          );
        }
      }

      return {
        generatedId: showtime.generatedId,
        startDate: processedStartDate,
        endDate: processedEndDate,
        eventDate: processedEventDate,
        eventCloseDoorDate: processedCloseDoorDate,
        prices: showtime.prices,

        isSoldOut: showtime?.isSoldOut || false,
        availableTickets: showtime?.availableTickets || 0,

        ...(showtime._id && { _id: showtime._id }),
      };
    });
  };
  const handleSelectedNewPlaceData = (data) => {
    const selectedEventPlaces = data.eventPlaces;

    setPlacesData(() => selectedEventPlaces);

    setFormDataValues((prevFormData) => {
      const updatedFormData = {
        ...prevFormData,
        eventPlace: selectedEventPlaces,
      };
      console.log("Current showtimes state in handleSelectedNewPlaceData:", showtimes);

      formValues({
        ...updatedFormData,
        isCinema: isCinemaMode,
        isCirque: isCirqueMode,
        eventDateList: buildEventDateList(showtimes, isCinemaMode, isCirqueMode),
      });

      return updatedFormData;
    });
  };

  const handleNewStandData = (data) => {
    setStandsData((prevData) => [...prevData, data]);
  };
  const handleSelectedNewStandData = (data) => {
    const selectedEventStands = data.eventStand;

    setStandsData(() => selectedEventStands);

    setFormDataValues((prevFormData) => {
      const updatedFormData = {
        ...prevFormData,
        eventStand: selectedEventStands,
      };

      formValues({
        ...updatedFormData,
        isCinema: isCinemaMode,
        isCirque: isCirqueMode,
        eventDateList: buildEventDateList(showtimes, isCinemaMode, isCirqueMode),
      });

      return updatedFormData;
    });
  };

  const handleNewSiegeData = (data) => {
    setSiegesData((prevData) => [...prevData, data]);
  };
  const handleSelectedNewSiegeData = (data) => {
    const selectedEventSiege = data.eventSiege;

    setSiegesData(() => selectedEventSiege);

    setFormDataValues((prevFormData) => {
      const updatedFormData = {
        ...prevFormData,
        eventSiege: selectedEventSiege,
      };

      formValues({
        ...updatedFormData,
        isCinema: isCinemaMode,
        isCirque: isCirqueMode,
        eventDateList: buildEventDateList(showtimes, isCinemaMode, isCirqueMode),
      });

      return updatedFormData;
    });
  };

  const handleNewSocialData = (data) => {
    setSocialsData((prevData) => [...prevData, data]);
  };

  const handleEditePlacesListModal = () => {
    setOpenEventSelectedPlaces(true);
  };
  const handleEditeStandsListModal = () => {
    setOpenEventSelectedStand(true);
  };
  const handleEditeSiegeListModal = () => {
    setOpenEventSelectedSieges(true);
  };
  const handleEditeSocialListModal = () => {
    setOpenEventSelectedSocial(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormDataValues((prevFormData) => {
      const updatedFormData = {
        ...prevFormData,
        [name]: value,
      };

      formValues({
        ...updatedFormData,
        isCinema: isCinemaMode,
        isCirque: isCirqueMode,
        eventDateList: buildEventDateList(showtimes, isCinemaMode, isCirqueMode),
      });

      return updatedFormData;
    });
  };

  const handleIsOnlineSwitch = (onlineReservationVal) => {
    setFormDataValues((prevFormData) => {
      const updatedFormData = {
        ...prevFormData,
        onlineReservation: onlineReservationVal,
      };

      formValues({
        ...updatedFormData,
        isCinema: isCinemaMode,
        isCirque: isCirqueMode,
        eventDateList: buildEventDateList(showtimes, isCinemaMode, isCirqueMode),
      });

      return updatedFormData;
    });
  };

  const handleisIsSelectetivePlacesSwitch = (IsSelectetivePlacesValue) => {
    setFormDataValues((prevFormData) => {
      const updatedFormData = {
        ...prevFormData,
        isSelectetivePlaces: IsSelectetivePlacesValue,
      };

      formValues({
        ...updatedFormData,
        isCinema: isCinemaMode,
        isCirque: isCirqueMode,
        eventDateList: buildEventDateList(showtimes, isCinemaMode, isCirqueMode),
      });

      return updatedFormData;
    });
  };

  const handleisIsSoldOutSwitch = (isSoldOut) => {
    setFormDataValues((prevFormData) => {
      const updatedFormData = {
        ...prevFormData,
        isSoldOut: isSoldOut,
      };

      formValues({
        ...updatedFormData,
        isCinema: isCinemaMode,
        isCirque: isCirqueMode,
        eventDateList: buildEventDateList(showtimes, isCinemaMode, isCirqueMode),
      });

      return updatedFormData;
    });
  };

  const handleAutocompletePlaceChange = (e, newValue) => {
    setFormDataValues((prevFormData) => {
      const updatedFormData = {
        ...prevFormData,
        eventPlace: newValue,
      };

      formValues({
        ...updatedFormData,
        isCinema: isCinemaMode,
        isCirque: isCirqueMode,
        eventDateList: buildEventDateList(showtimes, isCinemaMode, isCirqueMode),
      });

      return updatedFormData;
    });
  };
  const handleAutocompleteCategoryChange = (e, newValue) => {
    setFormDataValues((prevFormData) => {
      const updatedFormData = {
        ...prevFormData,
        eventCategory: newValue,
      };

      formValues({
        ...updatedFormData,
        isCinema: isCinemaMode,
        isCirque: isCirqueMode,
        eventDateList: buildEventDateList(showtimes, isCinemaMode, isCirqueMode),
      });

      return updatedFormData;
    });
  };
  const handleAutocompleteStandChange = (e, newValue) => {
    setFormDataValues((prevFormData) => {
      const updatedFormData = {
        ...prevFormData,
        eventStand: newValue,
      };

      formValues({
        ...updatedFormData,
        isCinema: isCinemaMode,
        isCirque: isCirqueMode,
        eventDateList: buildEventDateList(showtimes, isCinemaMode, isCirqueMode),
      });

      return updatedFormData;
    });
  };
  const handleAutocompleteSiegeChange = (event, newValue) => {
    setFormDataValues((prevFormData) => {
      const prevSelectedSieges = prevFormData.eventSiege || [];
      const currentSelectedSieges = newValue || [];

      const updatedFormData = {
        ...prevFormData,
        eventSiege: currentSelectedSieges,
      };

      const removedSieges = prevSelectedSieges.filter(
        (prevSiege) =>
          !currentSelectedSieges.some((currentSiege) => currentSiege.id === prevSiege.id)
      );

      setSiegesData((prevSiegesData) => {
        const updatedSiegesData = prevSiegesData.filter(
          (siege) => !removedSieges.some((removed) => removed.id === siege.id)
        );

        setShowtimes((prevShowtimes) => {
          const updatedShowtimes = prevShowtimes.map((showtime) => {
            const updatedPrices = (showtime.prices || []).filter((price) =>
              updatedSiegesData.some((availableSiege) => availableSiege.id === price.id)
            );
            return {
              ...showtime,
              prices: updatedPrices,
            };
          });

          const eventDateListData = buildEventDateList(updatedShowtimes, isCinemaMode, isCirqueMode);

          const parentPayload = {
            ...updatedFormData,
            isCinema: isCinemaMode,
        isCirque: isCirqueMode,
            eventDateList: eventDateListData,
            startDate: updatedFormData.startDate,
            startTime: updatedFormData.startTime,
            endDate: updatedFormData.endDate,
            endTime: updatedFormData.endTime,
            eventDate: updatedFormData.eventDate,
            eventTime: updatedFormData.eventTime,
            eventDoorClosingDate: updatedFormData.eventDoorClosingDate,
            eventDoorClosingTime: updatedFormData.eventDoorClosingTime,
          };
          formValues(parentPayload);

          return updatedShowtimes;
        });

        return updatedSiegesData;
      });

      return updatedFormData;
    });
  };
  const handleAutocompleteSocialChange = (e, newValue) => {
    setFormDataValues((prevFormData) => {
      const updatedFormData = {
        ...prevFormData,
        socialLink: newValue,
      };

      formValues({
        ...updatedFormData,
        isCinema: isCinemaMode,
        isCirque: isCirqueMode,
        eventDateList: buildEventDateList(showtimes, isCinemaMode, isCirqueMode),
      });

      return updatedFormData;
    });
  };

  const handleRefreshData = () => {
    // getInitialData()
    // alert("OK")
  };
  const handleShowtimeInputChange = (index, event) => {
    const { name, type, checked, value } = event.target;

    const inputValue = type === "checkbox" ? checked : value; // Use `checked` for checkboxes (like Switch)

    setShowtimes((prevShowtimes) => {
      const updatedShowtimes = [...prevShowtimes];
      updatedShowtimes[index] = { ...updatedShowtimes[index], [name]: inputValue };

      formValues({
        ...formDataValues,
        isCinema: isCinemaMode,
        isCirque: isCirqueMode,
        eventDateList: buildEventDateList(updatedShowtimes, isCinemaMode, isCirqueMode),
      });

      return updatedShowtimes;
    });
  };
  const handleRemoveShowtime = (index) => {
    setShowtimes((prevShowtimes) => {
      const updatedShowtimes = prevShowtimes.filter((_, i) => i !== index);

      formValues({
        ...formDataValues,
        isCinema: isCinemaMode,
        isCirque: isCirqueMode,
        eventDateList: buildEventDateList(updatedShowtimes, isCinemaMode, isCirqueMode),
      });

      return updatedShowtimes;
    });
  };
  const handleShowtimePriceChange = (index, selectedPricesArray) => {
    setShowtimes((prevShowtimes) => {
      const updatedShowtimes = [...prevShowtimes];
      const showtimeToUpdate = { ...updatedShowtimes[index] };

      showtimeToUpdate.prices = selectedPricesArray;

      updatedShowtimes[index] = showtimeToUpdate;

      const eventDateListData = buildEventDateList(updatedShowtimes, isCinemaMode, isCirqueMode);

      const parentPayload = {
        ...formDataValues,
        isCinema: isCinemaMode,
        isCirque: isCirqueMode,
        eventDateList: eventDateListData,
        startDate: formDataValues.startDate,
        startTime: formDataValues.startTime,
        endDate: formDataValues.endDate,
        endTime: formDataValues.endTime,
        eventDate: formDataValues.eventDate,
        eventTime: formDataValues.eventTime,
        eventDoorClosingDate: formDataValues.eventDoorClosingDate,
        eventDoorClosingTime: formDataValues.eventDoorClosingTime,
      };

      formValues(parentPayload);

      return updatedShowtimes;
    });
  };

  const removeSelectedCinemaHour = (index) => {
    try {
    } catch (error) {
      console.log(error);
    }
  };

  const handleUpdateCinemaSiegeData = (siege, timeIndex, showtime) => {
    try {
      siege.map((el) => {
        el.fromGeneratedId = showtime.generatedId;
      });

      // check if siege is already exist from seleted time id
      const errors = {
        existingSiegeName: false,
        isDuplicated: false,
        emptySiege: false,
        siegeName: null,
      };

      if (siege.length == 0) errors.emptySiege = true;

      if (errors.emptySiege) {
        return Swal.fire({
          title: `Ajouter une siége`,
          text: "",
          icon: "error",
        });
      }

      const findDuplicates = (array) => {
        const seen = new Set();
        const duplicates = new Set();

        array.forEach((item) => {
          if (seen.has(item)) {
            duplicates.add(item);
          } else {
            seen.add(item);
          }
        });

        return [...duplicates];
      };

      const selectedSiegesName = siege
        .filter((el) => el.fromGeneratedId === showtime.generatedId)
        .map((el) => el.name);
      if (findDuplicates(selectedSiegesName).length > 0) {
        errors.siegeName = findDuplicates(selectedSiegesName)[0];
        return Swal.fire({
          title: `Ce siège existe déjà (${errors.siegeName})`,
          text: "",
          icon: "error",
        });
      }

      // const addedSiegeName = siegesData.filter(el => el.fromGeneratedId === showtime.generatedId).map(el => el.name)
      // siege.filter(el => el.fromGeneratedId === showtime.generatedId).map(el => {
      //   if(addedSiegeName.includes(el.name)) {
      //     errors.existingSiegeName = true
      //     errors.siegeName = el.name
      //   }
      // })

      // if(errors.existingSiegeName) {
      //   return Swal.fire({
      //     title: `Ce siège existe déjà (${errors.siegeName})`,
      //     text: "",
      //     icon: "error",
      //   });
      // }

      const selectedSiegeId = siege[0].fromGeneratedId;
      const ERRORS = {
        dateError: false,
      };

      const selectedShowTimesArray = [];

      setShowtimes((prevShowTimes) => {
        // Create a new array to avoid mutating the original state
        const updatedShowTimes = prevShowTimes.map((itemInState) => {
          if (itemInState.generatedId === selectedSiegeId) {
            // Update the prices for the matching showtime
            return {
              ...itemInState,
              prices: siege,
              availableTickets: 0,
            };
          }

          // Return the original showtime if no changes are needed
          return {
            ...itemInState,

            availableTickets: 0,
          };
        });

        selectedShowTimesArray.push(updatedShowTimes);
        return updatedShowTimes; // Return the new array
      });

      if (ERRORS.dateError) {
        return Swal.fire({
          title: `Entrer tous les dates`,
          text: "",
          icon: "error",
        });
      }

      // Update the siegesData array
      setSiegesData((prevSiegesData) => {
        const updatedPrevData = prevSiegesData.filter(
          (el) => el.fromGeneratedId !== showtime.generatedId
        );
        const updatedSiegesDataArray = [...updatedPrevData, ...siege];

        // Update formDataValues with the new sieges array
        setFormDataValues((prevFormData) => {
          const finalShowtimesForEventDateList = selectedShowTimesArray.flat(1);
          const updatedFormData = {
            ...prevFormData,
            isCinema: isCinemaMode,
            isCirque: isCirqueMode,
            eventDateList: buildEventDateList(finalShowtimesForEventDateList, isCinemaMode, isCirqueMode),
            eventSiege: updatedSiegesDataArray, // Update the eventSiege field
          };

          // updatedFormData.eventSiege = updatedFormData.eventSiege.flat(1)

          // Send the updated formDataValues to the parent
          formValues(updatedFormData);
          return updatedFormData;
        });

        return updatedSiegesDataArray;
      });

      // console.log("showtimes: ", showtimes)
      // console.log("siegesData: ", siegesData)
      // console.log("formDataValues: ", formDataValues)

      return Swal.fire({
        title: "Modifiée",
        text: "",
        icon: "success",
      });
    } catch (error) {
      console.log(error);
    }
  };

  const DATE_SOLDOUT_FIELDS = [
    "isSoldOut",
    "startDate",
    "startTime",
    "endDate",
    "endTime",
    "eventDate",
    "eventTime",
    "eventDoorClosingDate",
    "eventDoorClosingTime",
  ];

  const isShowtimeDirty = (index) => {
    const initial = initialShowtimesRef.current[index];
    if (!initial) return false;
    const current = showtimes[index];
    return DATE_SOLDOUT_FIELDS.some((field) => current[field] !== initial[field]);
  };

  return (
    <Card>
      <EventCategories
        openEventCategory={open}
        closeEventCategory={handleClose}
        refreshData={handleRefreshData}
        isEdite={false}
        openModalItemData={null}
      />
      <EventPlaces
        openEventPlace={openEventPlaces}
        closeEventPlace={handleCloseEventPlaces}
        refreshData={handleRefreshData}
        isEdite={false}
        openModalItemData={null}
        newPlaceData={handleNewPlaceData}
      />
      <EventSieges
        openEventSiege={openEventSieges}
        closeEventSiege={handleCloseEventSieges}
        refreshData={handleRefreshData}
        isEdite={false}
        openModalItemData={null}
        newSiegeData={handleNewSiegeData}
      />
      <EventStands
        openEventStand={openEventStand}
        closeEventStand={handleCloseEventStands}
        refreshData={handleRefreshData}
        isEdite={false}
        openModalItemData={null}
        newStandData={handleNewStandData}
      />
      <EventSocial
        openEventSocial={openEventSocial}
        closeEventSocial={handleCloseEventSocial}
        refreshData={handleRefreshData}
        isEdite={false}
        openModalItemData={null}
        newSocialData={handleNewSocialData}
      />

      <EventSelectedPlaces
        openEventSelectedPlace={openEventSelectedPlaces}
        closeEventSelectedPlace={handleCloseSelectedEventPlaces}
        refreshData={handleRefreshData}
        isEdite={false}
        openModalItemData={formDataValues.eventPlace}
        newPlaceData={handleSelectedNewPlaceData}
      />
      <EventSelectedStands
        openEventSelectedStand={openEventSelectedStand}
        closeEventSelectedStand={handleCloseSelectedEventStands}
        refreshData={handleRefreshData}
        isEdite={false}
        openModalItemData={formDataValues.eventStand}
        newStandData={handleSelectedNewStandData}
      />
      <EventSelectedSieges
        openEventSelectedSiege={openEventSelectedSieges}
        closeEventSelectedSiege={handleCloseSelectedEventSieges}
        refreshData={handleRefreshData}
        isEdite={false}
        openModalItemData={formDataValues.eventSiege}
        newSiegeData={handleSelectedNewSiegeData}
      />

      <MDBox p={3}>
        <MDBox mb={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="city-select-label">Ville</InputLabel>
                <Select
                  labelId="city-select-label"
                  name="city"
                  value={formDataValues.city || ""}
                  onChange={handleInputChange}
                  label="Ville"
                >
                  <MenuItem value=""><em>— Sélectionner une ville —</em></MenuItem>
                  {cities.map((c) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </MDBox>
        <MDTypography variant="h5">Informations sur l'événement</MDTypography>
        <MDBox mt={1}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormField
                type="text"
                label="Titre de l'événement"
                name="eventTitle"
                value={formDataValues.eventTitle}
                onChange={handleInputChange}
              />
            </Grid>
          </Grid>
        </MDBox>

        <MDBox mt={1}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormField
                type="text"
                label="Vidéo promotionnelle"
                name="promoVideo"
                value={formDataValues.promoVideo}
                onChange={handleInputChange}
              />
            </Grid>
          </Grid>
        </MDBox>

        <MDBox mt={1}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormField
                type="text"
                label="Adresse"
                name="address"
                value={formDataValues.address}
                onChange={handleInputChange}
              />
            </Grid>
          </Grid>
        </MDBox>

        <MDBox mt={1}>
          <Grid container xs={12}>
            <Grid item xs={12} md={6}>
              <MDBox>
                <MDBox display="inline-block">
                  <MDTypography
                    component="label"
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    textTransform="capitalize"
                  >
                    Catégories
                  </MDTypography>
                </MDBox>
                <Autocomplete
                  multiple
                  value={formDataValues?.eventCategory}
                  options={categoriesData}
                  getOptionLabel={(option) => option.name}
                  onChange={handleAutocompleteCategoryChange}
                  renderInput={(params) => (
                    <FormField {...params} InputLabelProps={{ shrink: true }} />
                  )}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} sx={{ mt: { xs: 0, md: 5 } }}>
              <MDButton color="success" variant="text" onClick={handleAddNewCategoryModal}>
                Ajouter une nouvelle catégorie
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>

        <MDBox mt={1}>
          <Grid container xs={12}>
            <Grid item xs={12} md={6}>
              <MDBox>
                <MDBox display="inline-block">
                  <Icon
                    fontSize="medium"
                    color="success"
                    style={{
                      cursor: "pointer",
                      position: "relative",
                      top: "5px",
                      marginRight: "10px",
                    }}
                    onClick={handleEditePlacesListModal}
                  >
                    edit
                  </Icon>
                  <MDTypography
                    component="label"
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    textTransform="capitalize"
                  >
                    Lieu de l'événement :
                  </MDTypography>
                </MDBox>
                <Autocomplete
                  multiple
                  value={formDataValues?.eventPlace}
                  // defaultValue={[]}
                  options={placesData}
                  getOptionLabel={(option) => option.name}
                  onChange={handleAutocompletePlaceChange}
                  renderInput={(params) => (
                    <FormField {...params} InputLabelProps={{ shrink: true }} />
                  )}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} sx={{ mt: { xs: 0, md: 5 } }}>
              {/* <MDButton color="success" variant="text" onClick={handleAddNewPlaceModal}>
                Ajouter un nouveau lieu
              </MDButton> */}
            </Grid>
          </Grid>
        </MDBox>

        <MDBox mt={1}>
          <Grid container xs={12}>
            <Grid item xs={12} md={6}>
              <MDBox>
                <MDBox display="inline-block">
                  <Icon
                    fontSize="medium"
                    color="success"
                    style={{
                      cursor: "pointer",
                      position: "relative",
                      top: "5px",
                      marginRight: "10px",
                    }}
                    onClick={handleEditeStandsListModal}
                  >
                    edit
                  </Icon>
                  <MDTypography
                    component="label"
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    textTransform="capitalize"
                  >
                    Stands
                  </MDTypography>
                </MDBox>
                <Autocomplete
                  multiple
                  value={formDataValues?.eventStand}
                  options={standsData}
                  getOptionLabel={(option) => option.name}
                  onChange={handleAutocompleteStandChange}
                  renderInput={(params) => (
                    <FormField {...params} InputLabelProps={{ shrink: true }} />
                  )}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} sx={{ mt: { xs: 0, md: 5 } }}>
              {/* <MDButton color="success" variant="text" onClick={handleAddNewResellerModal}>
                Ajouter un nouveau stand
              </MDButton> */}
            </Grid>
          </Grid>
        </MDBox>

        <MDBox mt={1}>
          <Grid container xs={12}>
            <Grid item xs={12} md={6}>
              <MDBox>
                <MDBox display="inline-block">
                  <Icon
                    fontSize="medium"
                    color="success"
                    style={{
                      cursor: "pointer",
                      position: "relative",
                      top: "5px",
                      marginRight: "10px",
                    }}
                    onClick={handleEditeSiegeListModal}
                  >
                    edit
                  </Icon>
                  <MDTypography
                    component="label"
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    textTransform="capitalize"
                  >
                    Siège (VIP / Normal ...)
                  </MDTypography>
                </MDBox>
                <Autocomplete
                  multiple
                  value={formDataValues?.eventSiege}
                  options={siegesData}
                  getOptionLabel={(option) => option.name}
                  onChange={handleAutocompleteSiegeChange}
                  renderInput={(params) => (
                    <FormField {...params} InputLabelProps={{ shrink: true }} />
                  )}
                  disabled
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} sx={{ mt: { xs: 0, md: 5 } }}>
              {/* <MDButton color="success" variant="text" onClick={handleAddNewSiegeModal}>
                Ajouter un nouveau siège
              </MDButton> */}
            </Grid>
          </Grid>
        </MDBox>

        <MDBox mt={1}>
          <Grid container xs={12}>
            <Grid item xs={12} md={6}>
              <MDBox>
                <MDBox display="inline-block">
                  <MDTypography
                    component="label"
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    textTransform="capitalize"
                  >
                    Réseaux sociaux
                  </MDTypography>
                </MDBox>
                <Autocomplete
                  multiple
                  value={formDataValues?.socialLink}
                  options={socialsData}
                  getOptionLabel={(option) => option.name}
                  onChange={handleAutocompleteSocialChange}
                  renderInput={(params) => (
                    <FormField {...params} InputLabelProps={{ shrink: true }} />
                  )}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} sx={{ mt: { xs: 0, md: 5 } }}>
              <MDButton color="success" variant="text" onClick={handleAddNewsocialModal}>
                Ajouter
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>

        <MDBox mt={1}>
          <FormControlLabel
            control={
              <Switch
                checked={formDataValues.onlineReservation}
                onChange={() => handleIsOnlineSwitch(!formDataValues.onlineReservation)}
              />
            }
            label="Résérvation online"
          />
        </MDBox>

        <MDBox mt={1}>
          <Grid container spacing={3} xs={12}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formDataValues.isSoldOut}
                    onChange={() => handleisIsSoldOutSwitch(!formDataValues.isSoldOut)}
                  />
                }
                label="Soldout"
              />
            </Grid>
          </Grid>
        </MDBox>

        <MDBox mt={1}>
          <Grid container spacing={3} xs={12}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formDataValues.isSelectetivePlaces}
                    onChange={() =>
                      handleisIsSelectetivePlacesSwitch(!formDataValues.isSelectetivePlaces)
                    }
                  />
                }
                label="Réservation via le plan"
              />
            </Grid>
            {formDataValues.isSelectetivePlaces && (
              <Grid item xs={12} md={6}>
                <FormField
                  type="text"
                  label="Seat.io id"
                  name="seatIoId"
                  value={formDataValues.seatIoId}
                  onChange={handleInputChange}
                />
              </Grid>
            )}
          </Grid>
        </MDBox>
        <MDBox mt={1}>
          <Grid container spacing={3} xs={12}>
            <Grid item xs={12} md={6}>
              <MDBox mt={1}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isCinemaMode}
                      onChange={(e) => {
                        const newValue = e.target.checked;
                        setIsCinemaMode(newValue);
                        if (newValue) setIsCirqueMode(false);

                        if (newValue) {
                          setSiegesData([]);
                          const newFormDataValues = {
                            ...formDataValues,
                            eventSiege: [],
                          };
                          setFormDataValues(newFormDataValues);

                          setShowtimes([
                            {
                              generatedId: uuidv4(),
                              startDate: "",
                              startTime: "",
                              endDate: "",
                              endTime: "",
                              eventDate: "",
                              eventTime: "",
                              eventDoorClosingDate: "",
                              eventDoorClosingTime: "",
                              isSoldOut: false,
                            },
                          ]);
                        }

                        const eventDateListData = newValue
                          ? showtimes.map((showtime) => ({
                              startDate: showtime.startDate
                                ? new Date(
                                    showtime.startDate + "T" + (showtime.startTime || "00:00")
                                  )
                                : null,
                              endDate: showtime.endDate
                                ? new Date(showtime.endDate + "T" + (showtime.endTime || "00:00"))
                                : null,
                              eventDate: showtime.eventDate
                                ? new Date(
                                    showtime.eventDate + "T" + (showtime.eventTime || "00:00")
                                  )
                                : null,
                              eventCloseDoorDate: showtime.eventDoorClosingDate
                                ? new Date(
                                    showtime.eventDoorClosingDate +
                                      "T" +
                                      (showtime.eventDoorClosingTime || "00:00")
                                  )
                                : null,
                            }))
                          : [];

                        formValues({
                          ...formDataValues,
                          isCinema: newValue,
                          isCirque: false,
                          eventDateList: eventDateListData,
                        });
                      }}
                    />
                  }
                  label="Mode Cinéma"
                />
              </MDBox>
              <MDBox mt={1}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isCirqueMode}
                      disabled={isCinemaMode}
                      onChange={(e) => {
                        const newValue = e.target.checked;
                        setIsCirqueMode(newValue);

                        if (newValue) {
                          setSiegesData([]);
                          setFormDataValues({ ...formDataValues, eventSiege: [] });
                          setShowtimes([
                            {
                              generatedId: uuidv4(),
                              startDate: "",
                              startTime: "",
                              endDate: "",
                              endTime: "",
                              eventDate: "",
                              eventTime: "",
                              eventDoorClosingDate: "",
                              eventDoorClosingTime: "",
                              isSoldOut: false,
                            },
                          ]);
                        }

                        const eventDateListData = newValue
                          ? showtimes.map((showtime) => ({
                              startDate: showtime.startDate
                                ? new Date(showtime.startDate + "T" + (showtime.startTime || "00:00"))
                                : null,
                              endDate: showtime.endDate
                                ? new Date(showtime.endDate + "T" + (showtime.endTime || "00:00"))
                                : null,
                              eventDate: showtime.eventDate
                                ? new Date(showtime.eventDate + "T" + (showtime.eventTime || "00:00"))
                                : null,
                              eventCloseDoorDate: showtime.eventDoorClosingDate
                                ? new Date(
                                    showtime.eventDoorClosingDate +
                                      "T" +
                                      (showtime.eventDoorClosingTime || "00:00")
                                  )
                                : null,
                            }))
                          : [];

                        formValues({
                          ...formDataValues,
                          isCirque: newValue,
                          isCinema: false,
                          eventDateList: eventDateListData,
                        });
                      }}
                    />
                  }
                  label="Mode Cirque"
                />
              </MDBox>
            </Grid>
            {formDataValues.isSelectetivePlaces && (
              <Grid item xs={12} md={6}>
                <FormField
                  type="text"
                  label="Seat.io id"
                  name="seatIoId"
                  value={formDataValues.seatIoId}
                  onChange={handleInputChange}
                />
              </Grid>
            )}
          </Grid>
        </MDBox>
        {(isCinemaMode || isCirqueMode) ? (
          <Grid item xs={12}>
            <MDBox mt={3}>
              {showtimes.map((showtime, index) => (
                <MDBox
                  key={index}
                  mb={2}
                  sx={{ border: "1px solid #ccc", padding: 2, borderRadius: 1 }}
                >
                  <MDTypography variant="h6">Séance {index + 1}</MDTypography>
                  <MDBox>
                    <FormControlLabel
                      control={
                        <Switch
                          name="isSoldOut"
                          checked={showtime.isSoldOut || false}
                          onChange={(e) => handleShowtimeInputChange(index, e)}
                        />
                      }
                      label="Soldout"
                    />
                  </MDBox>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <MDBox mb={2}>
                        <MDTypography component="label" variant="caption" fontWeight="bold">
                          Date de début
                        </MDTypography>
                        <Input
                          type="date"
                          name="startDate"
                          value={showtime.startDate}
                          onChange={(e) => handleShowtimeInputChange(index, e)}
                          fullWidth
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDBox mb={2}>
                        <MDTypography component="label" variant="caption" fontWeight="bold">
                          Heure de début
                        </MDTypography>
                        <Input
                          type="time"
                          name="startTime"
                          value={showtime.startTime}
                          onChange={(e) => handleShowtimeInputChange(index, e)}
                          fullWidth
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDBox mb={2}>
                        <MDTypography component="label" variant="caption" fontWeight="bold">
                          Date de fin
                        </MDTypography>
                        <Input
                          type="date"
                          name="endDate"
                          value={showtime.endDate}
                          onChange={(e) => handleShowtimeInputChange(index, e)}
                          fullWidth
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDBox mb={2}>
                        <MDTypography component="label" variant="caption" fontWeight="bold">
                          Heure de fin
                        </MDTypography>
                        <Input
                          type="time"
                          name="endTime"
                          value={showtime.endTime}
                          onChange={(e) => handleShowtimeInputChange(index, e)}
                          fullWidth
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDBox mb={2}>
                        <MDTypography component="label" variant="caption" fontWeight="bold">
                          Date de l'événement
                        </MDTypography>
                        <Input
                          type="date"
                          name="eventDate"
                          value={showtime.eventDate}
                          onChange={(e) => handleShowtimeInputChange(index, e)}
                          fullWidth
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDBox mb={2}>
                        <MDTypography component="label" variant="caption" fontWeight="bold">
                          Heure de l'événement
                        </MDTypography>
                        <Input
                          type="time"
                          name="eventTime"
                          value={showtime.eventTime}
                          onChange={(e) => handleShowtimeInputChange(index, e)}
                          fullWidth
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDBox mb={2}>
                        <MDTypography component="label" variant="caption" fontWeight="bold">
                          Date de fermeture des portes
                        </MDTypography>
                        <Input
                          type="date"
                          name="eventDoorClosingDate"
                          value={showtime.eventDoorClosingDate}
                          onChange={(e) => handleShowtimeInputChange(index, e)}
                          fullWidth
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDBox mb={2}>
                        <MDTypography component="label" variant="caption" fontWeight="bold">
                          Heure de fermeture des portes
                        </MDTypography>
                        <Input
                          type="time"
                          name="eventDoorClosingTime"
                          value={showtime.eventDoorClosingTime}
                          onChange={(e) => handleShowtimeInputChange(index, e)}
                          fullWidth
                        />
                      </MDBox>
                    </Grid>
                    {isShowtimeDirty(index) && (
                      <Grid item xs={12}>
                        <MDButton
                          variant="gradient"
                          color="info"
                          onClick={() =>
                            Swal.fire({ title: "Modifiée", text: "", icon: "success" })
                          }
                        >
                          Modifier
                        </MDButton>
                      </Grid>
                    )}
                    <Grid item xs={12} md={12}>
                      <MDBox mb={1}>
                        <MDTypography component="label" variant="caption" fontWeight="bold">
                          Siège (VIP / Normal ...)
                        </MDTypography>
                        {/* <Autocomplete
                          multiple
                          options={formDataValues.eventSiege || []}
                          getOptionLabel={(option) => option.name}
                          value={
                            siegesData.filter((siege) =>
                              showtime.prices?.some(
                                (selectedPrice) => selectedPrice.id === siege.id
                              )
                            ) || []
                          }
                          onChange={(event, newValue) => handleShowtimePriceChange(index, newValue)}
                          renderInput={(params) => (
                            <MDInput
                              {...params}
                              label="Siège (VIP / Normal ...)"
                              InputLabelProps={{ shrink: true }}
                            />
                          )}
                        /> */}
                        {/* fromGeneratedId */}
                        {/* <p>{JSON.stringify(showtime.generatedId)}</p> */}
                        <CinemaSelectedSieges
                          isEdite={false}
                          openModalItemData={siegesData.filter(
                            (el) => el.fromGeneratedId == showtime.generatedId
                          )}
                          newSiegeData={(siege, timeIndex) =>
                            handleUpdateCinemaSiegeData(siege, timeIndex, showtime)
                          }
                          // newSiegeData={setSiegesData}
                          selectedTimeIndex={index}
                        />
                      </MDBox>
                    </Grid>
                    {showtimes.length > 1 && (
                      <MDBox
                        mt={1}
                        sx={{
                          cursor: "pointer",
                          display: "flex",
                          justifyContent: "flex-center",
                          marginLeft: "460px",
                          marginBottom: "-80px",
                        }}
                      >
                        <Icon
                          color="error"
                          fontSize="medium"
                          onClick={() => handleRemoveShowtime(index)}
                        >
                          delete
                        </Icon>
                      </MDBox>
                    )}
                  </Grid>
                  {(isCinemaMode || isCirqueMode) && (
                    <MDBox
                      mt={1}
                      sx={{
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "flex-center",
                        marginLeft: "465px",
                      }}
                    >
                      <Icon
                        color="success"
                        fontSize="medium"
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          setShowtimes([
                            ...showtimes,
                            {
                              generatedId: uuidv4(),
                              startDate: "",
                              startTime: "",
                              endDate: "",
                              endTime: "",
                              eventDate: "",
                              eventTime: "",
                              eventDoorClosingDate: "",
                              eventDoorClosingTime: "",
                              isSoldOut: false,
                            },
                          ])
                        }
                      >
                        add
                      </Icon>
                    </MDBox>
                  )}
                </MDBox>
              ))}
            </MDBox>
          </Grid>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <MDBox mb={2}>
                <MDTypography component="label" variant="caption" fontWeight="bold">
                  Date de début
                </MDTypography>
                <Input
                  type="date"
                  name="startDate"
                  value={formDataValues.startDate}
                  onChange={handleInputChange}
                  fullWidth
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <MDBox mb={2}>
                <MDTypography component="label" variant="caption" fontWeight="bold">
                  Heure de début
                </MDTypography>
                <Input
                  type="time"
                  name="startTime"
                  value={formDataValues.startTime}
                  onChange={handleInputChange}
                  fullWidth
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <MDBox mb={2}>
                <MDTypography component="label" variant="caption" fontWeight="bold">
                  Date de fin
                </MDTypography>
                <Input
                  type="date"
                  name="endDate"
                  value={formDataValues.endDate}
                  onChange={handleInputChange}
                  fullWidth
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <MDBox mb={2}>
                <MDTypography component="label" variant="caption" fontWeight="bold">
                  Heure de fin
                </MDTypography>
                <Input
                  type="time"
                  name="endTime"
                  value={formDataValues.endTime}
                  onChange={handleInputChange}
                  fullWidth
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <MDBox mb={2}>
                <MDTypography component="label" variant="caption" fontWeight="bold">
                  Date de l'événement
                </MDTypography>
                <Input
                  type="date"
                  name="eventDate"
                  value={formDataValues.eventDate}
                  onChange={handleInputChange}
                  fullWidth
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <MDBox mb={2}>
                <MDTypography component="label" variant="caption" fontWeight="bold">
                  Heure de l'événement
                </MDTypography>
                <Input
                  type="time"
                  name="eventTime"
                  value={formDataValues.eventTime}
                  onChange={handleInputChange}
                  fullWidth
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <MDBox mb={2}>
                <MDTypography component="label" variant="caption" fontWeight="bold">
                  Date de fermeture des portes
                </MDTypography>
                <Input
                  type="date"
                  name="eventDoorClosingDate"
                  value={formDataValues.eventDoorClosingDate}
                  onChange={handleInputChange}
                  fullWidth
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <MDBox mb={2}>
                <MDTypography component="label" variant="caption" fontWeight="bold">
                  Heure de fermeture des portes
                </MDTypography>
                <Input
                  type="time"
                  name="eventDoorClosingTime"
                  value={formDataValues.eventDoorClosingTime}
                  onChange={handleInputChange}
                  fullWidth
                />
              </MDBox>
            </Grid>
          </Grid>
        )}
      </MDBox>
    </Card>
  );
}

FormFields.propTypes = {
  formValues: PropTypes.string.isRequired,
};

export default FormFields;
